import React, { useCallback } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { Paprika } from '../../../../styles/theme';
import { TimeChip } from '../primitives/TimeChip';
import { CookedStamp } from '../../CookedStamp';
import { ApprovalThumbs } from '../../ApprovalThumbs';
import { CookRow } from '../../CookRow';
import { useVerdictPrompt } from '../../../household/VerdictPromptProvider';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../../redux/mealPlanSlice';
import { RemoteImage } from '../../../shared/RemoteImage';
import { RecipePlaceholder } from '../../../shared/RecipePlaceholder';

/**
 * The day, drawn inside the card instead of above it — for the common day that
 * holds one dinner and nothing else. Tapping it opens the day up, which is
 * where its empty déjeuner and dîner are.
 */
export interface MealRowDayTag {
  /** Short weekday, e.g. `ven`. */
  short: string;
  /** Day of the month. */
  num: number;
  /** Spoken label for the tap target that opens the day. */
  a11yLabel: string;
  onPress: () => void;
  testID?: string;
}

interface Props {
  recipe: RecipeIndexEntry;
  /** Empty when the day tag or the day header already says what meal this is. */
  mealLabel: string;
  dietaryRole?: DietaryRole;
  isCooked?: boolean;
  /** Number of dishes planned in this slot — shown next to the label when > 1. */
  dishCount?: number;
  /** Committed servings for this meal — shown in the tappable portions badge. */
  servings?: number;
  /** Planned meal id — required to record an approval. */
  mealId?: string;
  dayTag?: MealRowDayTag;
  /** Opens the recipe detail sheet — the whole card (photo included) is the target. */
  onPhotoPress: () => void;
  /** Opens the replace (fan) sheet. */
  onShufflePress: () => void;
  /** Opens the move sheet (#563). */
  onMovePress: () => void;
  /** Opens the portions adjuster sheet. */
  onPortionsPress?: () => void;
  /**
   * Opens the accompaniment list (#746). Given only while the slot can take
   * one and has none yet: once a side is listed under the card, the way to add
   * another is there.
   */
  onAddSidePress?: () => void;
}

const THUMB = 56;

/**
 * Compact meal card (#722): thumbnail, title, a line of meta, and the two
 * actions as icons on the right.
 *
 * It used to be a full-width photo card, about 240px tall — on a 344×582 phone
 * the first screen held one dinner, and the cook who filed #722 could not see
 * the plan they had just been given. At ~80px, four dinners fit above the fold.
 * Every control the big card carried is still here: move, replace (or propose,
 * in a paired household), portions, approval thumbs, who cooks, and the cooked
 * stamp, which now sits on the thumbnail.
 */
export function MealRow({ recipe, mealLabel, dishCount, servings, isCooked, mealId, dayTag, onPhotoPress, onShufflePress, onMovePress, onPortionsPress, onAddSidePress }: Props) {
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'mobile-row');
  const { status: householdStatus } = useHouseholdMembers();
  const { openVerdict } = useVerdictPrompt();

  // The stamp is a display of what the table said; tapping asks them.
  const handleRate = useCallback(() => {
    openVerdict({ recipeId: recipe.id, recipeTitle: recipe.title, surface: 'cooked_stamp' });
  }, [openVerdict, recipe.id, recipe.title]);

  // An icon on the card, so this is its only name — spoken, and the one a
  // harness finds it by.
  const replaceLabel = householdStatus === 'paired' ? t('household.approval.propose') : t('thisWeek.replace');
  const showLabelRow = !!mealLabel || (dishCount != null && dishCount > 1);

  return (
    <View style={styles.wrap}>
      {/* CookRow renders nothing outside a paired household, so a dinner-only
          day costs no label row at all. */}
      <View style={[styles.labelRow, !showLabelRow && styles.labelRowBare]}>
        {mealLabel ? <Text style={styles.mealLabel}>{mealLabel.toUpperCase()}</Text> : <View />}
        {dishCount != null && dishCount > 1 && (
          <Text style={styles.dishCount}>{t('thisWeek.dishCount', { count: dishCount })}</Text>
        )}
        <CookRow mealId={mealId} variant="inline" />
      </View>
      {/* No button role on the card: on web it would render a <button>, and
          the icons inside it would be buttons in a button. */}
      <Pressable style={styles.card} onPress={onPhotoPress} accessibilityLabel={title}>
        {dayTag && (
          <Pressable
            style={({ pressed }) => [styles.dayTag, pressed && styles.dayTagPressed]}
            onPress={(e) => {
              e.stopPropagation?.();
              dayTag.onPress();
            }}
            accessibilityRole="button"
            accessibilityLabel={dayTag.a11yLabel}
            testID={dayTag.testID}
          >
            <Text style={styles.dayTagShort}>{dayTag.short.toUpperCase()}</Text>
            <Text style={styles.dayTagNum}>{dayTag.num}</Text>
          </Pressable>
        )}

        <View style={styles.photoWrap} testID="meal-photo">
          <RemoteImage
            uri={recipe.image}
            variant="thumb"
            style={styles.photo}
            resizeMode="cover"
            fallback={<RecipePlaceholder recipe={recipe} size={THUMB} style={styles.photo} />}
          />
          {/* Cooked stamp overlay — shows who at the table liked it, read from
              the recipe's verdicts so it agrees with every other surface. */}
          {isCooked && (
            <CookedStamp recipeId={recipe.id} size="dense" onRate={handleRate} />
          )}
        </View>

        <View style={styles.body}>
          <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">{title}</Text>
          <View style={styles.metaRow}>
            {recipe.readyInMinutes != null && <TimeChip minutes={recipe.readyInMinutes} />}
            {servings != null && onPortionsPress && (
              <Pressable
                style={styles.portions}
                onPress={(e) => {
                  e.stopPropagation?.();
                  onPortionsPress();
                }}
                accessibilityRole="button"
                accessibilityLabel={t('thisWeek.servingsShort', { count: servings })}
                hitSlop={6}
              >
                <Feather name="users" size={11} color={Paprika.textSecondary} />
                <Text style={styles.portionsText}>{servings}</Text>
              </Pressable>
            )}
            {/* On the folded card, where the cook is looking — « Ajouter un
                plat » used to wait in the opened day, behind a tap on the day
                tag that nothing announced (#746). A pill, not a row: the list
                keeps the density #722 bought. */}
            {onAddSidePress && (
              <Pressable
                style={({ pressed }) => [styles.addSide, pressed && styles.addSidePressed]}
                onPress={(e) => {
                  e.stopPropagation?.();
                  onAddSidePress();
                }}
                accessibilityRole="button"
                accessibilityLabel={t('thisWeek.addSideLong')}
                testID="meal-add-side"
                hitSlop={6}
              >
                <Feather name="plus" size={11} color={Paprika.primary} />
                <Text style={styles.addSideText}>{t('thisWeek.addSide')}</Text>
              </Pressable>
            )}
            <ApprovalThumbs mealId={mealId} variant="outline" />
          </View>
        </View>

        {/* Move and replace, as icons. "Déplacer" sits next to "Remplacer"
            because move used to live two sheets deep behind it (#563). */}
        <View style={styles.actions}>
          <Pressable
            style={({ pressed }) => [styles.actionBtn, pressed && styles.actionBtnPressed]}
            onPress={(e) => {
              e.stopPropagation?.();
              onMovePress();
            }}
            accessibilityRole="button"
            accessibilityLabel={t('thisWeek.move')}
            hitSlop={4}
          >
            <Feather name="calendar" size={14} color={Paprika.textSecondary} />
          </Pressable>
          <Pressable
            style={({ pressed }) => [styles.actionBtn, pressed && styles.actionBtnPressed]}
            onPress={(e) => {
              e.stopPropagation?.();
              onShufflePress();
            }}
            accessibilityRole="button"
            accessibilityLabel={replaceLabel}
            testID="meal-replace"
            hitSlop={4}
          >
            <Feather name="refresh-cw" size={14} color={Paprika.textSecondary} />
          </Pressable>
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    gap: 4,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  labelRowBare: {
    minHeight: 0,
  },
  mealLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.6,
    color: Paprika.textMuted,
    paddingLeft: 2,
  },
  dishCount: {
    fontFamily: 'Inter-Medium',
    fontSize: 10.5,
    color: Paprika.textMuted,
    paddingRight: 2,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.10)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  dayTag: {
    width: 40,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  dayTagPressed: {
    backgroundColor: Paprika.surfaceLow,
  },
  dayTagShort: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.6,
    color: Paprika.textMuted,
  },
  dayTagNum: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 21,
    lineHeight: 24,
    color: Paprika.textPrimary,
  },
  photoWrap: {
    width: THUMB,
    height: THUMB,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: Paprika.bgSunk,
  },
  photo: {
    ...StyleSheet.absoluteFillObject,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 15.5,
    lineHeight: 19,
    color: Paprika.textPrimary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  portions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: Paprika.surfaceLow,
  },
  portionsText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  addSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: Paprika.brandTint,
  },
  addSidePressed: {
    backgroundColor: Paprika.surfaceLow,
  },
  addSideText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.primary,
  },
  actions: {
    gap: 6,
  },
  actionBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.surfaceLow,
  },
  actionBtnPressed: {
    backgroundColor: Paprika.brandTint,
  },
});
